import { supabase } from '@/lib/supabase';

export interface CreateSubscriptionInput {
  country: string;
  paymentMethodId?: string;
}

export interface CreateSubscriptionResponse {
  success: boolean;
  already_active?: boolean;
  subscription_id: string;
  status: string;
  payment_intent_status?: string | null;
  payment_intent_client_secret?: string | null;
}

export type ManageSubscriptionAction = 'billing_portal' | 'cancel_at_period_end' | 'resume';

/**
 * Categorical cancellation reason (SOS-334 step 1).
 * Must match the CHECK constraint in `subscriptions.cancellation_reason`.
 */
export type CancellationReason =
  | 'too_little_usage'
  | 'not_worth_price'
  | 'roll_reviews_not_reaching'
  | 'missing_feature'
  | 'other';

export interface ManageSubscriptionParams {
  action: ManageSubscriptionAction;
  /** Only meaningful when action === 'cancel_at_period_end'. Optional. */
  reason?: CancellationReason | null;
}

export interface ManageSubscriptionResponse {
  url?: string;
  success?: boolean;
  cancel_at_period_end?: boolean;
  status?: string;
  /** 'at_period_end' (normal) or 'immediate' (past_due). Only present on cancel. */
  mode?: 'at_period_end' | 'immediate' | 'resume';
  reason_captured?: boolean;
}

export const subscriptionApi = {
  async create(input: CreateSubscriptionInput): Promise<CreateSubscriptionResponse> {
    const { data, error } = await supabase.functions.invoke('stripe-create-subscription', {
      body: {
        country: input.country,
        payment_method_id: input.paymentMethodId,
      },
    });

    if (error) throw new Error(error.message);
    return data as CreateSubscriptionResponse;
  },

  async manageSubscription(
    actionOrParams: ManageSubscriptionAction | ManageSubscriptionParams,
  ): Promise<ManageSubscriptionResponse> {
    const params: ManageSubscriptionParams =
      typeof actionOrParams === 'string' ? { action: actionOrParams } : actionOrParams;

    const session = await supabase.auth.getSession();
    const token = session.data.session?.access_token;
    if (!token) throw new Error('Not authenticated');

    const baseUrl = import.meta.env.VITE_SUPABASE_URL;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    const res = await fetch(`${baseUrl}/functions/v1/stripe-manage-subscription`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        apikey: anonKey,
      },
      body: JSON.stringify({
        action: params.action,
        ...(params.reason ? { reason: params.reason } : {}),
      }),
    });

    const payload = (await res.json()) as ManageSubscriptionResponse & {
      error?: string;
      code?: string;
    };

    if (!res.ok) {
      throw Object.assign(new Error(payload.error || `Request failed (${res.status})`), {
        ...(typeof payload.code === 'string' ? { code: payload.code } : {}),
      });
    }

    return payload;
  },
};
